// src/pages/Watchlist.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../utils/api';

const fmt = v => (v == null ? '—' : Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

function StatusBadge({ status }) {
  const cls = status === 'active' ? 'badge badge-green' : 'badge badge-gray';
  return <span className={cls}>{status || 'active'}</span>;
}

function EditableCell({ value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const start = () => { setDraft(value ?? ''); setEditing(true); };
  const cancel = () => setEditing(false);
  const save = () => {
    const n = parseFloat(draft);
    if (!isNaN(n) && n !== value) onSave(n);
    setEditing(false);
  };

  if (editing) {
    return (
      <input
        className="inline-edit-input"
        autoFocus
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') cancel(); }}
      />
    );
  }
  return (
    <span
      title="Click to edit"
      style={{ cursor: 'pointer', borderBottom: '1px dashed #cbd5e1' }}
      onClick={start}
    >
      {fmt(value)}
    </span>
  );
}

export default function Watchlist() {
  const [rows, setRows]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);
  const [form, setForm]     = useState({ symbol: '', entry_price: '', stop_loss: '', trail_stop_loss: '' });
  const [adding, setAdding] = useState(false);
  const [msg, setMsg]       = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    authFetch('/api/prebuy_watchlist')
      .then(r => r.json())
      .then(setRows)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const flash = (text, type = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 3000);
  };

  const updateField = async (symbol, field, value) => {
    try {
      const r = await authFetch(
        `/api/prebuy_watchlist/update?symbol=${encodeURIComponent(symbol)}&field=${field}&value=${value}`,
        { method: 'POST' }
      );
      if (!r.ok) throw new Error(await r.text());
      setRows(prev => prev.map(row => row.symbol === symbol ? { ...row, [field]: value } : row));
    } catch (e) {
      flash(e.message, 'error');
    }
  };

  const deleteRow = async symbol => {
    if (!window.confirm(`Remove ${symbol} from watchlist?`)) return;
    try {
      const r = await authFetch(`/api/prebuy_watchlist/${encodeURIComponent(symbol)}`, { method: 'DELETE' });
      if (!r.ok) throw new Error(await r.text());
      setRows(prev => prev.filter(row => row.symbol !== symbol));
      flash(`${symbol} removed`);
    } catch (e) {
      flash(e.message, 'error');
    }
  };

  const addEntry = async e => {
    e.preventDefault();
    if (!form.symbol.trim()) return;
    setAdding(true);
    try {
      const body = {
        symbol: form.symbol.trim().toUpperCase(),
        entry_price:      form.entry_price      ? parseFloat(form.entry_price)      : null,
        stop_loss:        form.stop_loss        ? parseFloat(form.stop_loss)        : null,
        trail_stop_loss:  form.trail_stop_loss  ? parseFloat(form.trail_stop_loss)  : null,
      };
      const r = await authFetch('/api/prebuy_watchlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error(await r.text());
      setForm({ symbol: '', entry_price: '', stop_loss: '', trail_stop_loss: '' });
      flash(`${body.symbol} added`);
      load();
    } catch (e) {
      flash(e.message, 'error');
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Watchlist</h1>
        <p className="page-subtitle">Pre-buy candidates with entry levels and stop losses</p>
      </div>

      {msg && (
        <div className={`alert alert-${msg.type === 'error' ? 'error' : 'success'}`}>
          {msg.text}
        </div>
      )}

      {/* Add form */}
      <div className="card" style={{ marginBottom: '1.25rem' }}>
        <p className="card-title">Add Symbol</p>
        <form onSubmit={addEntry}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Symbol *</label>
              <input
                className="form-input"
                placeholder="e.g. RELIANCE"
                value={form.symbol}
                onChange={e => setForm(p => ({ ...p, symbol: e.target.value.toUpperCase() }))}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Entry Price</label>
              <input
                className="form-input"
                type="number"
                step="0.05"
                placeholder="0.00"
                value={form.entry_price}
                onChange={e => setForm(p => ({ ...p, entry_price: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Stop Loss</label>
              <input
                className="form-input"
                type="number"
                step="0.05"
                placeholder="0.00"
                value={form.stop_loss}
                onChange={e => setForm(p => ({ ...p, stop_loss: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Trail Stop</label>
              <input
                className="form-input"
                type="number"
                step="0.05"
                placeholder="0.00"
                value={form.trail_stop_loss}
                onChange={e => setForm(p => ({ ...p, trail_stop_loss: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label className="form-label">&nbsp;</label>
              <button className="btn btn-primary" type="submit" disabled={adding}>
                {adding ? <span className="spinner" /> : 'Add'}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
          <span className="spinner" style={{ width: 24, height: 24 }} />
        </div>
      ) : error ? (
        <div className="alert alert-error">{error}</div>
      ) : rows.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', color: '#94a3b8', padding: '2rem' }}>
          No symbols in watchlist. Add one above.
        </div>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Symbol</th>
                <th className="num">Entry Price</th>
                <th className="num">Stop Loss</th>
                <th className="num">Trail Stop</th>
                <th>Status</th>
                <th>Added</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.symbol}>
                  <td style={{ fontWeight: 600 }}>{row.symbol}</td>
                  <td className="num">
                    <EditableCell value={row.entry_price} onSave={v => updateField(row.symbol, 'entry_price', v)} />
                  </td>
                  <td className="num">
                    <EditableCell value={row.stop_loss} onSave={v => updateField(row.symbol, 'stop_loss', v)} />
                  </td>
                  <td className="num">
                    <EditableCell value={row.trail_stop_loss} onSave={v => updateField(row.symbol, 'trail_stop_loss', v)} />
                  </td>
                  <td><StatusBadge status={row.status} /></td>
                  <td className="text-muted" style={{ fontSize: '0.78rem' }}>
                    {row.created_at ? new Date(row.created_at).toLocaleDateString('en-IN') : '—'}
                  </td>
                  <td>
                    <button className="btn btn-danger btn-sm" onClick={() => deleteRow(row.symbol)}>
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p style={{ marginTop: '0.6rem', fontSize: '0.75rem', color: '#94a3b8' }}>
        Click any price cell to edit inline. Press Enter to save, Escape to cancel.
      </p>
    </div>
  );
}
